import { useMemo } from 'react';
import { z } from 'zod';
import {
    PaymentTransactionFlowType,
    formatTimestampForMerchantUi,
    type GatewayTransactionType,
    type GetVaultTransactionsResponse,
    type VaultTransactionListItem,
} from '@pillar-portal/api-types';
import {
    PaymentReconciliationStatus,
    PaymentTransactionStatus,
} from '@pillar-portal/database/dist/frontend';
import { paymentsApi } from '@/lib/pillarApi';
import { formatMonetaryAmount } from '@/lib/utils';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListPhysicalJsonStructurePredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { AlertTriangle } from 'lucide-react';
import { getTransactionDataQualityPresentation } from './transaction-data-quality';

interface TransactionsListContentProps {
    pageTitle: string;
    cardTitle?: string;
    loadingLabel: string;
    endOfListLabel: string;
    amountColumnVisible: boolean;
    detailBasePath: '/payment-transactions' | '/card-vault';
    gatewayTransactionTypes: ReadonlyArray<GatewayTransactionType>;
}

type TransactionsQueryKey = readonly ['payments', 'vaultTransactions', string, string, string];

const PaymentTransactionFlowTypeLabel: Record<PaymentTransactionFlowType, string> = {
    [PaymentTransactionFlowType.TERMINAL]: 'Terminal',
    [PaymentTransactionFlowType.CARD_NOT_PRESENT]: 'Card Not Present',
    [PaymentTransactionFlowType.E_INVOICE]: 'E-Invoice',
    [PaymentTransactionFlowType.ACH]: 'ACH',
};

const PaymentTransactionFieldPredicateDescriptors: readonly InfiniteScrollListFieldPredicateDescriptor[] =
    [
        {
            field: 'merchant_display_name',
            label: 'Merchant',
            operator: 'eq',
            valueType: 'string',
        },
        {
            field: 'status',
            label: 'Status',
            operator: 'in',
            valueType: 'string',
            options: Object.values(PaymentTransactionStatus).map((status) => ({
                value: status,
                label: status,
            })),
        },
        {
            field: 'reconciled',
            label: 'Reconciliation',
            operator: 'in',
            valueType: 'string',
            options: Object.values(PaymentReconciliationStatus).map((status) => ({
                value: status,
                label: status,
            })),
        },
        {
            field: 'transaction_type',
            label: 'Transaction type',
            operator: 'in',
            valueType: 'string',
            options: Object.values(PaymentTransactionFlowType).map((flowType) => ({
                value: flowType,
                label: PaymentTransactionFlowTypeLabel[flowType],
            })),
        },
    ];

const PaymentTransactionPhysicalJsonStructurePredicateDescriptors: readonly InfiniteScrollListPhysicalJsonStructurePredicateDescriptor[] =
    [
        { field: 'transaction_configuration', label: 'Transaction configuration' },
        { field: 'amount_breakdown', label: 'Amount breakdown' },
        { field: 'ipw_transaction_details', label: 'Website transaction details' },
    ];

export function TransactionsListContent({
    pageTitle,
    cardTitle,
    loadingLabel,
    endOfListLabel,
    amountColumnVisible,
    detailBasePath,
    gatewayTransactionTypes,
}: TransactionsListContentProps) {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryKeyPrefix: TransactionsQueryKey = [
        'payments',
        'vaultTransactions',
        gatewayTransactionTypes.join(','),
        amountColumnVisible ? 'amount-visible' : 'amount-hidden',
        detailBasePath,
    ];

    const renderDataQualityIndicator = (item: VaultTransactionListItem) => {
        if (item.data_quality === null) {
            return null;
        }
        const presentation = getTransactionDataQualityPresentation(item.data_quality);
        return (
            <span
                title={presentation.tooltip}
                aria-label={presentation.ariaLabel}
                className="inline-flex items-center"
            >
                <AlertTriangle className={presentation.severityClassName} />
            </span>
        );
    };

    const columns: Array<Column<VaultTransactionListItem>> = useMemo(() => {
        const baseColumns: Array<Column<VaultTransactionListItem>> = [
            {
                key: 'transaction_created_ts',
                header: 'Timestamp',
                sortable: true,
                render: (item) =>
                    formatTimestampForMerchantUi(
                        item.transaction_created_ts,
                        item.merchant_timezone
                    ),
            },
            {
                key: 'merchant_transaction_id',
                header: 'Transaction',
                sortable: true,
                render: (item) => (
                    <div className="flex items-center gap-2">
                        {renderDataQualityIndicator(item)}
                        <span>{item.merchant_transaction_id}</span>
                    </div>
                ),
            },
            {
                key: 'merchant_user_display_name',
                header: 'User',
                sortable: true,
                render: (item) => {
                    const userUrl = `/users/${item.merchant_user_uuid}`;
                    return (
                        <a
                            className="text-primary underline-offset-4 hover:underline"
                            href={userUrl}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: userUrl,
                                        title: 'User Details',
                                    },
                                });
                            }}
                        >
                            {item.merchant_user_display_name}
                        </a>
                    );
                },
            },
            {
                key: 'merchant_display_name',
                header: 'Merchant',
                render: (item) => {
                    const merchantUrl = `/organizations/${item.merchant_organization_uuid}`;
                    return (
                        <a
                            className="text-primary underline-offset-4 hover:underline"
                            href={merchantUrl}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: merchantUrl,
                                        title: 'Organization Details',
                                    },
                                });
                            }}
                        >
                            {item.merchant_display_name}
                        </a>
                    );
                },
            },
            {
                key: 'customer_name',
                header: 'Customer',
                render: (item) => item.customer_name ?? '—',
            },
            {
                key: 'cardholder_name',
                header: 'Cardholder',
                render: (item) => item.cardholder_name ?? '—',
            },
            {
                key: 'card_last4',
                header: 'Last4',
                render: (item) => item.card_last4 ?? '—',
            },
            {
                key: 'transaction_type',
                header: 'Type',
                render: (item) => PaymentTransactionFlowTypeLabel[item.transaction_type],
            },
        ];

        if (amountColumnVisible) {
            baseColumns.push(
                {
                    key: 'base_amount',
                    header: 'Base Amount',
                    sortable: true,
                    render: (item) => (
                        <div className="monetary-amount">
                            {item.base_amount !== null
                                ? formatMonetaryAmount(item.base_amount)
                                : '—'}
                        </div>
                    ),
                },
                {
                    key: 'original_amount',
                    header: 'Total Amount',
                    sortable: true,
                    render: (item) => (
                        <div className="monetary-amount">
                            {formatMonetaryAmount(item.original_amount)}
                        </div>
                    ),
                }
            );
        }

        baseColumns.push(
            {
                key: 'status',
                header: 'Status',
                sortable: true,
                render: (item) => (
                    <Badge variant={item.status === 'FAILED' ? 'destructive' : 'default'}>
                        {item.status}
                    </Badge>
                ),
            },
            {
                key: 'auth_decision_ts',
                header: 'Authorized At',
                sortable: true,
                render: (item) =>
                    item.auth_decision_ts !== null
                        ? formatTimestampForMerchantUi(
                              item.auth_decision_ts,
                              item.merchant_timezone
                          )
                        : '—',
            }
        );

        return baseColumns;
    }, [amountColumnVisible, breadcrumbNavigate, renderDataQualityIndicator]);

    return (
        <InfiniteScrollList<VaultTransactionListItem, string, TransactionsQueryKey>
            pageTitle={pageTitle}
            cardTitle={cardTitle}
            queryPlaceholder="Omnisearch (customer/invoice/gateway tx id)..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            controlsPlacement="above-search"
            predicateControls={{
                fieldPredicates: PaymentTransactionFieldPredicateDescriptors,
                jsonPathPredicates: PaymentTransactionPhysicalJsonStructurePredicateDescriptors,
            }}
            fetchPage={async (params) => {
                const response: GetVaultTransactionsResponse =
                    await paymentsApi.getVaultTransactions({
                        limit: params.limit,
                        gateway_transaction_types: [...gatewayTransactionTypes],
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        ...(params.fieldPredicates !== undefined
                            ? { field_predicates: [...params.fieldPredicates] }
                            : {}),
                        ...(params.jsonPathPredicates !== undefined
                            ? { json_path_predicates: [...params.jsonPathPredicates] }
                            : {}),
                        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
                    });
                return { items: response.transactions, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected string cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel,
                endOfListLabel,
                emptyLabel: 'No transactions found.',
                genericErrorLabel: 'Failed to load transactions.',
            }}
            renderItems={({ items }) => (
                <DataTable
                    data={[...items]}
                    columns={columns}
                    keyExtractor={(item) => item.payment_transaction_uuid}
                    paginationMode="none"
                    onRowClick={(item) => {
                        breadcrumbNavigate({
                            source: 'panel',
                            target: {
                                url: `${detailBasePath}/${item.payment_transaction_uuid}`,
                                title: 'Transaction Details',
                            },
                        });
                    }}
                />
            )}
        />
    );
}
